import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Capacitor } from "@capacitor/core";
import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import DeleteIcon from "@mui/icons-material/Delete";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PhotoIcon from "@mui/icons-material/Photo";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import {
  Box,
  Button,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import type { EventFileEntry } from "model";
import {
  FILE_UPLOAD_TYPE,
  useCreateEventFileEntry,
  useCreateFileEntry,
  useDeleteEventFileEntry,
  useEventFiles,
} from "model";
import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { uploadFile } from "services";
import { useAppStore } from "stores";

type StagedFile = {
  id: string;
  fileName: string;
  base64: string;
  uploadType: FILE_UPLOAD_TYPE;
};

export type EventFileUploadHandle = {
  uploadStagedFiles: (farmEventGuid: string) => Promise<void>;
  hasStagedFiles: () => boolean;
};

interface Props {
  farmEventGuid?: string;
}

const EventFileUpload = forwardRef<EventFileUploadHandle, Props>(
  function EventFileUpload({ farmEventGuid }, ref) {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { notify } = useNotification();
    const user = useAppStore((state) => state.user);
    const createFileEntry = useCreateFileEntry();
    const createEventFileEntry = useCreateEventFileEntry();
    const deleteEventFileEntry = useDeleteEventFileEntry();
    const { data: existingFiles = [] } = useEventFiles(farmEventGuid);

    // Staged files for when farmEventGuid doesn't exist yet
    const [stagedFiles, setStagedFiles] = useState<StagedFile[]>([]);

    const isStaged = !farmEventGuid;

    const uploadAndLink = async (
      fileName: string,
      base64: string,
      uploadType: FILE_UPLOAD_TYPE,
      eventGuid: string
    ) => {
      if (!user?.guid) return;
      const fileUpload = await uploadFile({ fileName, file: base64 });
      const fileEntry = await createFileEntry.mutateAsync({
        fileDescription: "",
        fileUploadId: fileUpload.guid,
        associatedUserId: user.guid,
        fileUploadType: uploadType,
        userId: user.guid,
      });
      await createEventFileEntry.mutateAsync({
        fileEntryGuid: fileEntry.guid,
        farmEventGuid: eventGuid,
      });
    };

    // Expose upload function for staged mode
    useImperativeHandle(ref, () => ({
      uploadStagedFiles: async (eventGuid: string) => {
        for (const file of stagedFiles) {
          try {
            await uploadAndLink(
              file.fileName,
              file.base64,
              file.uploadType,
              eventGuid
            );
          } catch (err) {
            console.error("Failed to upload staged file:", err);
          }
        }
        setStagedFiles([]);
      },
      hasStagedFiles: () => stagedFiles.length > 0,
    }));

    const addFile = async (
      fileName: string,
      base64: string,
      uploadType: FILE_UPLOAD_TYPE
    ) => {
      if (isStaged) {
        setStagedFiles((prev) => [
          ...prev,
          { id: `staged-${Date.now()}`, fileName, base64, uploadType },
        ]);
      } else {
        if (!user?.guid) return;
        try {
          await uploadAndLink(fileName, base64, uploadType, farmEventGuid);
          notify("File attached");
        } catch (err) {
          console.error("Failed to attach file:", err);
          notify("Failed to attach file", "error");
        }
      }
    };

    const handleCameraPhoto = async (source: CameraSource) => {
      try {
        const image = await Camera.getPhoto({
          resultType: CameraResultType.DataUrl,
          source,
          quality: 90,
        });
        if (!image.dataUrl) return;
        const base64 = image.dataUrl.split(",")[1];
        const fileName = `event_${Date.now()}.${image.format || "jpeg"}`;
        await addFile(fileName, base64, FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO);
      } catch (err) {
        if (err instanceof Error && err.message?.includes("cancel")) return;
        console.error("Failed to capture photo:", err);
        notify("Failed to capture photo", "error");
      }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string).split(",")[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      await addFile(file.name, base64, FILE_UPLOAD_TYPE.ACTION_EVIDENCE_FILE);
      e.target.value = "";
    };

    const handleDeleteStaged = (id: string) => {
      setStagedFiles((prev) => prev.filter((f) => f.id !== id));
    };

    const handleDeleteExisting = async (item: EventFileEntry) => {
      if (!farmEventGuid) return;
      try {
        await deleteEventFileEntry.mutateAsync({
          guid: item.guid,
          farmEventGuid,
        });
        notify("File removed");
      } catch {
        notify("Failed to remove file", "error");
      }
    };

    const isUploading =
      createFileEntry.isPending || createEventFileEntry.isPending;

    const allFiles = isStaged
      ? stagedFiles.map((f) => ({
          key: f.id,
          name: f.fileName,
          isPhoto: f.uploadType === FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO,
          onDelete: () => handleDeleteStaged(f.id),
          canDelete: true,
        }))
      : existingFiles.map((f) => ({
          key: f.guid,
          name: f.fileName || "Unnamed file",
          isPhoto:
            f.fileUploadType ===
            FILE_UPLOAD_TYPE.ACTION_EVIDENCE_PHOTO.valueOf(),
          onDelete: () => handleDeleteExisting(f),
          canDelete: f.uploaderGuid === user?.guid,
        }));

    return (
      <Box>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <input
            ref={fileInputRef}
            type="file"
            hidden
            onChange={handleFileChange}
          />
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddPhotoAlternateIcon />}
            onClick={() => handleCameraPhoto(CameraSource.Photos)}
            disabled={isUploading}
            sx={{ textTransform: "none" }}
          >
            Photo
          </Button>
          {Capacitor.isNativePlatform() && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<CameraAltIcon />}
              onClick={() => handleCameraPhoto(CameraSource.Camera)}
              disabled={isUploading}
              sx={{ textTransform: "none" }}
            >
              Camera
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            startIcon={<UploadFileIcon />}
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            sx={{ textTransform: "none" }}
          >
            File
          </Button>
        </Box>

        {allFiles.length > 0 && (
          <List disablePadding sx={{ mt: 1.5 }}>
            {allFiles.map((item) => (
              <ListItem
                key={item.key}
                sx={{
                  border: "1px solid rgba(0, 0, 0, 0.12)",
                  borderRadius: "6px",
                  mb: 0.5,
                  pl: 1.5,
                  pr: item.canDelete ? "48px" : 1.5,
                  py: 0.5,
                }}
                secondaryAction={
                  item.canDelete ? (
                    <IconButton
                      size="small"
                      onClick={item.onDelete}
                      disabled={deleteEventFileEntry.isPending}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  ) : undefined
                }
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  {item.isPhoto ? (
                    <PhotoIcon sx={{ color: "#16a34a", fontSize: 20 }} />
                  ) : (
                    <InsertDriveFileIcon
                      sx={{ color: "#3b82f6", fontSize: 20 }}
                    />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={item.name}
                  primaryTypographyProps={{ fontSize: "13px", noWrap: true }}
                />
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    );
  }
);

export default EventFileUpload;
